/**
 * Left Nav Config Adapter — Pure API-driven
 *
 * All menu configuration comes from the API response.
 * No mock data, no fallbacks, no hardcoded mappings.
 * Menu structure is entirely dynamic based on API response.
 *
 * Endpoint: POST /ApplicationWorkspace/GetAll
 */

import { msalInitPromise, msalInstance } from '../auth/msalConfig';
import { environment, getStoredAiIntegrationId } from '../environments/environment';
import api from './api';
import type { DockButtonConfig, DockIconMode } from './dockService';
import type { DockFlyoutConfig, DockFlyoutKind } from 'kci-design-lib';

// ---------------------------------------------------------------------------
// Adapter interface
// ---------------------------------------------------------------------------

/**
 * Contract that every left-nav config adapter must satisfy.
 * Returns the raw (unfiltered) list of menu items for the current deployment.
 * Filtering (visibility, entitlement) is applied by dockService consumers.
 */
export interface LeftNavConfigAdapter {
  /**
   * Retrieves the full set of left-nav menu items before entitlement filtering.
   * Phase 2: the API endpoint may already apply server-side filtering; the
   * shape returned must still match DockButtonConfig[].
   */
  fetchRawConfig(): Promise<DockButtonConfig[]>;
}

/**
 * API response shape for Properties of each menu item.
 *
 * Key        — screen key sent by the API. For flyout items this is the flyout kind
 *              ('chats' | 'workspaces' | 'agents'); for navigate items it's a screen id.
 * FlyoutKind — when the API sends 'flyout' the button opens a flyout panel.
 *              The actual DockFlyoutKind is read from Key.
 */
interface LeftNavApiMenuProperties {
  IsEnable?: boolean;
  IsVisible?: boolean;
  Icon?: string;
  IconClass?: string;
  Key?: string;
  /** 'flyout' indicates the button opens a flyout; Key carries the panel kind */
  FlyoutKind?: string;
  IsSelected?: boolean;
  IsBottomMenu?: boolean;
}

interface LeftNavApiMenuItem {
  // API response fields (exact names from backend)
  Id: string;
  Name: string;
  IsActive: boolean;
  Properties?: LeftNavApiMenuProperties | null;
  Description?: string;
  Doctype?: string;
}

type LeftNavRequestPayload = Array<
  | { Language: string }
  | { key: string; value: string }
>;

const LEFT_NAV_API_BASE_URL =
  (import.meta.env.VITE_apiBaseUrl as string | undefined)?.trim() || '';
const LEFT_NAV_MENU_ICON_API = '/ApplicationWorkspace/GetAll';

const DEFAULT_LEFT_NAV_REQUEST_PAYLOAD: LeftNavRequestPayload = [
  // Match the legacy payload shape observed in the existing Admin Center UI.
  { Language: '' },
  { key: 'ctry', value: 'US' },
  { key: 'SCREEN.TYPE', value: 'Standalone' },
];

function joinGatewayAndEndpoint(gatewayBaseUrl: string, endpoint: string): string {
  const normalizedGateway = gatewayBaseUrl.trim().replace(/\/$/, '');
  const normalizedEndpoint = endpoint.trim();

  if (!normalizedGateway) {
    return normalizedEndpoint;
  }

  if (!normalizedEndpoint) {
    return normalizedGateway;
  }

  return `${normalizedGateway}${normalizedEndpoint.startsWith('/') ? '' : '/'}${normalizedEndpoint}`;
}

function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function withLeftNavHeaderAliases(headers: Record<string, string>): Record<string, string> {
  const normalized = { ...headers };

  const aiintegrationId = firstNonEmptyString(
    normalized.aiintegrationid,
    normalized['aiintegration-id'],
    normalized['x-aiintegration-id'],
    normalized.aiintegrationId,
  );

  const userId = firstNonEmptyString(normalized.userId, normalized['x-user-id']);
  const tenantId = firstNonEmptyString(normalized.tenantId, normalized['x-tenant-id']);

  if (aiintegrationId) {
    normalized.aiintegrationid = aiintegrationId;
    normalized['aiintegration-id'] = aiintegrationId;
    normalized['x-aiintegration-id'] = aiintegrationId;
    delete normalized.aiintegrationId;
  }

  if (userId) {
    normalized.userId = userId;
    normalized['x-user-id'] = userId;
  }

  if (tenantId) {
    normalized.tenantId = tenantId;
    normalized['x-tenant-id'] = tenantId;
  }

  return normalized;
}

async function buildLeftNavHeaders(): Promise<Record<string, string>> {
  await msalInitPromise;

  const activeAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
  const claims = (activeAccount?.idTokenClaims ?? {}) as Record<string, unknown>;

  const userId = firstNonEmptyString(
    activeAccount?.username,
    claims.preferred_username,
    claims.upn,
    claims.email,
    claims.unique_name,
    claims.oid,
    claims.sub,
    activeAccount?.localAccountId,
    typeof window !== 'undefined'
      ? (localStorage.getItem('userId') ?? localStorage.getItem('userid') ?? '')
      : '',
  );

  const tenantId = firstNonEmptyString(claims.tid, claims.tenantId);
  const aiintegrationId = firstNonEmptyString(
    getStoredAiIntegrationId(),
    import.meta.env.VITE_aiintegrationId,
    environment.aiIntegrationId,
  );

  const headers: Record<string, string> = {};

  if (aiintegrationId) {
    headers.aiintegrationid = aiintegrationId;
  }

  if (userId) {
    headers.userId = userId;
  }

  if (tenantId) {
    headers.tenantId = tenantId;
  }

  return withLeftNavHeaderAliases(headers);
}

// ---------------------------------------------------------------------------
// Flyout UI configurations — one per supported DockFlyoutKind.
// These define HOW the flyout panel looks/behaves, NOT the data inside it.
// Data is loaded dynamically by dockFlyoutDataService per kind.
//
// NOTE: Adding a new DockFlyoutKind requires updating kci-design-lib first
// (the TypeScript union type), then adding an entry here and in
// dockFlyoutDataService.ts (LOADER_BY_KIND). The adapter itself is fully
// dynamic — it reads FlyoutKind and Key directly from the API.
// ---------------------------------------------------------------------------

/** Shared defaults — each kind only overrides what differs. */
const BASE_FLYOUT_CONFIG = {
  isSearchVisible: true,
  isFilterIconVisible: false,
  isDropdownFilterVisible: false,
  isRowActionEnabled: true,
  searchPlaceholder: 'Search',
  pageSize: 20,
  hasFixedItems: false,
} satisfies Partial<DockFlyoutConfig>;

const FLYOUT_CONFIGS: Record<DockFlyoutKind, DockFlyoutConfig> = {
  chats: {
    ...BASE_FLYOUT_CONFIG,
    title: 'Chat History',
    structure: 'grouped',
    rowBehaviorMode: 'navigate',
    headerActions: [
      {
        id: 'chat-bulk-actions',
        icon: 'more_horiz',
        tooltip: 'Bulk Actions',
        variant: 'dropdown',
        actionType: 'custom',
        options: [
          {
            id: 'bulk-delete-chats',
            label: 'Delete All Unpinned Chats',
            variant: 'danger',
          },
          {
            id: 'bulk-download-files',
            label: 'Download All Uploaded Files',
          },
          {
            id: 'bulk-download-chats',
            label: 'Download All Chats',
          },
        ],
      },
      {
        id: 'new-chat',
        icon: 'add',
        tooltip: 'New Chat',
        variant: 'button',
        actionType: 'navigate',
        payload: {
          appId: 'tru-clara-ai',
          pageId: 'chat',
          forceReload: true,
        },
      },
    ],
    isDropdownFilterVisible: true,
    searchPlaceholder: 'Search',
    emptyStateText: "You don't have any tasks yet. Once you have created a project or start a task, it will be organized here.",
    loadingText: 'Loading chat history...',
    loadingMoreText: 'Loading more chats...',
    pageSize: 50,
  },
  workspaces: {
    ...BASE_FLYOUT_CONFIG,
    title: 'Workspaces',
    structure: 'nested',
    rowBehaviorMode: 'secondary',
    emptyStateText: "You don't have any workspaces yet.",
    loadingText: 'Loading workspaces...',
    loadingMoreText: 'Loading more workspaces...',
  },
  agents: {
    ...BASE_FLYOUT_CONFIG,
    title: 'Digital Workers',
    structure: 'flat',
    rowBehaviorMode: 'navigate',
    isFilterIconVisible: true,
    isDropdownFilterVisible: true,
    emptyStateText: '',
    loadingText: 'Loading agent activity...',
    loadingMoreText: 'Loading more agent activity...',
  },
};

const VALID_FLYOUT_KINDS = new Set<DockFlyoutKind>(['workspaces', 'agents', 'chats']);

/**
 * Resolve flyout vs navigate from Properties.FlyoutKind (sent by API as 'flyout').
 * When FlyoutKind === 'flyout', the DockFlyoutKind is read directly from Properties.Key
 * (the API now sends Key = 'chats' | 'workspaces' | 'agents' for flyout items).
 */
function resolveActionType(properties: LeftNavApiMenuProperties | null | undefined): {
  actionType: 'navigate' | 'flyout';
  kind: DockFlyoutKind | 'app';
  flyout: DockFlyoutConfig | undefined;
} {
  const isFlyout = (properties?.FlyoutKind ?? '').trim().toLowerCase() === 'flyout';
  if (isFlyout) {
    const key = (properties?.Key ?? '').trim();
    if (VALID_FLYOUT_KINDS.has(key as DockFlyoutKind)) {
      const kind = key as DockFlyoutKind;
      return { actionType: 'flyout', kind, flyout: FLYOUT_CONFIGS[kind] };
    }
  }
  return { actionType: 'navigate', kind: 'app', flyout: undefined };
}

// ---------------------------------------------------------------------------
// API Response Transformation
// ---------------------------------------------------------------------------

function resolveIconMode(icon: string | undefined): DockIconMode {
  const normalizedIcon = icon?.trim() ?? '';

  // Sprite reference (e.g. #symbol, /icons.svg#symbol, icons.svg#symbol)
  if (normalizedIcon.startsWith('#') || /\.svg#.+$/i.test(normalizedIcon)) {
    return 'sprite';
  }

  // Image file with extension (.svg, .png, .jpg, etc.)
  if (/\.(svg|png|jpe?g|gif|webp)$/i.test(normalizedIcon)) {
    return 'image';
  }

  // Material Design symbol (default)
  return 'material-symbol';
}

function normalizeSpriteIcon(icon: string): string {
  const normalizedIcon = icon.trim();

  if (!normalizedIcon) {
    return normalizedIcon;
  }

  // API can return #symbol; LeftMenu expects a resolvable sprite file path.
  if (normalizedIcon.startsWith('#')) {
    return `/icons.svg${normalizedIcon}`;
  }

  // Keep absolute URLs as-is.
  if (/^https?:\/\//i.test(normalizedIcon)) {
    return normalizedIcon;
  }

  // Ensure root-relative local sprite path when API sends icons.svg#symbol.
  if (!normalizedIcon.startsWith('/')) {
    return `/${normalizedIcon}`;
  }

  return normalizedIcon;
}

function sortMenuConfig(items: DockButtonConfig[]): DockButtonConfig[] {
  return [...items].sort((left, right) => (left.order ?? 0) - (right.order ?? 0));
}

/**
 * Transform API response directly to DockButtonConfig.
 * No merging, no mock data, no fallbacks.
 * API is the single source of truth.
 */
function transformApiResponseToDockButtons(remoteItems: LeftNavApiMenuItem[]): DockButtonConfig[] {
  console.log('[LeftNavAdapter] Raw API response items:', remoteItems.length);
  remoteItems.forEach((item, idx) => {
    console.log(`  [${idx}] Id="${item.Id}" Name="${item.Name}" Key="${item.Properties?.Key}" FlyoutKind="${item.Properties?.FlyoutKind}" Icon="${item.Properties?.Icon}" IsBottomMenu=${item.Properties?.IsBottomMenu}`);
  });

  const transformed = remoteItems
    .filter((item) => {
      const keep = item.IsActive && 
                   !item.Properties?.IsBottomMenu && 
                   (item.Properties?.IsVisible !== false) && 
                   (item.Properties?.IsEnable !== false);
      if (!keep) {
        console.log(`  ❌ Filtered out: "${item.Name}" (IsActive=${item.IsActive}, IsBottomMenu=${item.Properties?.IsBottomMenu}, IsVisible=${item.Properties?.IsVisible}, IsEnable=${item.Properties?.IsEnable})`);
      }
      return keep;
    })
    .map((item, idx) => {
      let icon = item.Properties?.Icon || '';
      const iconMode = resolveIconMode(icon);
      
      // Normalize sprite references so the SVG <use> element can resolve symbols.
      if (iconMode === 'sprite') {
        icon = normalizeSpriteIcon(icon);
      }

      const { actionType, kind, flyout } = resolveActionType(item.Properties);

      // Key is the screen identifier sent by the API.
      // Used directly as id and homePage — no frontend mapping applied.
      const key = item.Properties?.Key || item.Id;
      
      return {
        id: key,
        label: item.Name,
        kind,
        icon: icon,
        iconMode,
        iconClassName: item.Properties?.IconClass?.trim() || '',
        iconFallback: '',
        homePage: key,
        titleKey: item.Id,
        actionType: key === 'agents' ? 'navigate' : actionType,
        flyout,
        order: idx,
        // DO NOT REMOVE OR OVERRIDE Condition: key === 'workspaces'
        limitedDeploymentOnly: key === 'guides' || key === 'workspaces',
        isConfigurable: true,
        isEnabled: item.Properties?.IsEnable ?? true,
        isVisible: item.Properties?.IsVisible ?? true,
      } as DockButtonConfig;
    });

  console.log('[LeftNavAdapter] Transformed to DockButtonConfig:', transformed.length);
  transformed.forEach((btn) => {
    console.log(`  ✓ id="${btn.id}" label="${btn.label}" icon="${btn.icon}" iconMode="${btn.iconMode}" homePage="${btn.homePage}" actionType="${btn.actionType}" kind="${btn.kind}"`);
  });
  return sortMenuConfig(transformed);
}

// ---------------------------------------------------------------------------
// API Adapter — Pure API-driven, no fallbacks
// ---------------------------------------------------------------------------

/**
 * API adapter — calls the ApplicationWorkspace endpoint.
 *
 * Endpoint:  POST /ApplicationWorkspace/GetAll
 * Auth:      Bearer token (MSAL) forwarded by the shared API layer
 * Response:  LeftNavApiMenuItem[] → transformed directly to DockButtonConfig[]
 *
 * No fallback. API is the only source of truth.
 */
export function createApiLeftNavConfigAdapter(baseUrl: string): LeftNavConfigAdapter {
  return {
    fetchRawConfig: async () => {
      const requestHeaders = await buildLeftNavHeaders();

      const response = await api.fetch<LeftNavApiMenuItem[]>(baseUrl, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(DEFAULT_LEFT_NAV_REQUEST_PAYLOAD),
        headers: requestHeaders,
      });
      return transformApiResponseToDockButtons(response);
    },
  };
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Returns the active left-nav config adapter.
 * Pure API-driven mode — no fallbacks, no mock data.
 * API URL must be configured.
 */
export function createLeftNavConfigAdapter(): LeftNavConfigAdapter {
  const requestUrl = joinGatewayAndEndpoint(LEFT_NAV_API_BASE_URL, LEFT_NAV_MENU_ICON_API);

  if (!requestUrl) {
    throw new Error(
      'LEFT_NAV_API_BASE_URL is not configured. Cannot initialize menu adapter.'
    );
  }

  return createApiLeftNavConfigAdapter(requestUrl);
}
